<template>
  <div class="hidden lg:flex gap-2 lg:gap-4 2xl:gap-8 grow items-center">
    <div v-for="item in items" :key="item.label">
      <!-- The regExp in data-testid replaces all whitespaces with - symbols -->
      <MainMenuLink
        v-if="!item.items?.length"
        :item="item"
        :data-testid="`menu-item-${item.label.replace(/\s+/g, '-').toLowerCase()}`" />
      <MainMenuFlyout v-else :to="item.to" :label="$t(item.label)" :region="item.region">
        <div class="border-t border-t-border bg-surface menu-sub text-surface-foreground">
          <MainMenuLinks :item="item" />
        </div>
      </MainMenuFlyout>
    </div>
  </div>

  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-50 text-foreground" @close="closeMenu">
      <DialogBackground />
      <div class="fixed inset-0 overflow-hidden overflow-x-hidden z-50">
        <div class="h-full sm:h-auto sm:min-h-full flex justify-start items-start text-center text-foreground">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 lg:duration-700"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-500 lg:duration-700"
            leave-from="translate-x-0"
            leave-to="-translate-x-full">
            <DialogPanel
              class="w-full h-dvh sm:max-w-[400px] bg-mobile-menu text-search-forecolor text-left align-middle shadow-xl transition-all text-foreground pt-safe pb-safe flex flex-col gap-6 overflow-y-auto">
              <div class="flex justify-between items-center border-b border-slate-300 pb-4 mb-4 px-4">
                <span class="font-medium" @click="closeMenu">{{ $t("Close menu") }}</span>
                <button class="px-3 py-2 text-slate-800" @click="closeMenu">
                  <XMarkIcon class="h-5 inline" />
                </button>
              </div>
              <div class="grid gap-6 px-4">
                <MainMenuSubMenu v-for="item in items" :key="item.label" :item="item" @close="closeMenu" />
                <span class="flex justify-between font-medium cursor-pointer w-full" @click="openContactForm = true">
                  {{ $t("Customer Support") }}
                </span>
              </div>
              <ContactForm :open="openContactForm" @hide-modal="openContactForm = false" />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { MainMenuFlyout, MainMenuLink, MainMenuLinks } from ".";
import ContactForm from "@/components/ContactForm.vue";

import { ref, watch, computed } from "vue";
import brandconfig from "@/brandconfig.ts";
import { toEvent } from "@/components/event";
import { toTeam, toTournament } from "@/components/events";
// import { useEventsStore } from "@/store/events.js";
// import { useSportTypesStore } from "@/store/sportTypes.js";
// import { useTeamsStore } from "@/store/teams.js";
import { useRoute } from "vue-router";
import MainMenuSubMenu from "./MainMenuMobile.vue";
import { useTournaments } from "@/helpers/tournaments";
import { useEvents } from "@/helpers/events";
import { useTeams } from "@/helpers/teams";
import type { MenuItem } from "./menuItem";
import { XMarkIcon, GlobeAltIcon } from "@heroicons/vue/24/outline";
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from "@headlessui/vue";
import DialogBackground from "@/components/shared/DialogBackground.vue";

defineProps<{
  open: boolean;
}>();

const emit = defineEmits(["close"]);

const closeMenu = () => {
  emit("close");
};

const searchMenuKey = ref(0);
const route = useRoute();
const openContactForm = ref(false);

watch(route, () => {
  searchMenuKey.value++;
  closeMenu();
});

const TODAY = new Date().toISOString().substr(0, 10);

const queryParams = brandconfig.menu.reduce((current, menuItem) => {
  menuItem.submenu?.forEach((submenu) => {
    const key = submenu.type;

    if (!current.has(key)) {
      current.set(key, new Map<string, Set<string>>());
    }

    const typeGroup = current.get(key)!;

    Object.entries(submenu.params).forEach(([key, value]) => {
      const actualKey = `${key}`;

      if (!typeGroup.has(actualKey)) {
        typeGroup.set(actualKey, new Set<string>());
      }

      if (typeof value === "string") {
        typeGroup.get(actualKey)!.add(value);
      } else {
        (value as string[]).forEach((val) => {
          typeGroup.get(actualKey)!.add(val);
        });
      }
    });
  });

  return current;
}, new Map<string, Map<string, Set<string>>>());

const tournamentsQuery =
  queryParams.get("tournaments") &&
  useTournaments({
    ...Object.fromEntries(
      Array.from(queryParams.get("tournaments")!.entries()).map(([key, value]) => [
        key,
        `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    date_stop: "ge:" + TODAY,
    page_size: 9999,
  });

const popularEventsQuery =
  queryParams.get("popular-events") &&
  useEvents({
    ...Object.fromEntries(
      Array.from(queryParams.get("popular-events")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    date_stop: "ge:" + TODAY,
    sorting: "date_stop:asc",
    page_size: 5,
    popular_events: true,
    tickets_available: "ge:1",
    event_status: "in:[nosale,notstarted,postponed]",
  });

const upcomingEventsQuery =
  queryParams.get("upcoming-events") &&
  useEvents({
    ...Object.fromEntries(
      Array.from(queryParams.get("upcoming-events")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    date_stop: "ge:" + TODAY,
    sorting: "date_stop:asc",
    page_size: 5,
    popular_events: false,
    tickets_available: "ge:1",
    event_status: "in:[nosale,notstarted,postponed]",
  });

const popularTeamsQuery =
  queryParams.get("popular-teams") &&
  useTeams({
    ...Object.fromEntries(
      Array.from(queryParams.get("popular-teams")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    page_size: 9999,
    popular: true,
    event_startdate: "ge:2025-02-04",
  });

const items = computed<MenuItem[] | undefined>(() => {
  const queries = [tournamentsQuery, popularEventsQuery, upcomingEventsQuery, popularTeamsQuery];

  if (queries.some((q) => q !== undefined && !q.data.value)) return;

  return brandconfig.menu
    .map((menu) => {
      // Set to property for sports with tournaments submenu to navigate to tournaments page
      // Only allow Tennis and Football, exclude "Other events" as it's not a sport type but a category
      let menuTo = menu.to;
      if (!menuTo && menu.submenu?.some((s) => s.type === "tournaments") && menu.label !== "Other events") {
        const sportType = menu.label.toLowerCase();
        // Only allow tournaments pages for Tennis and Football
        if (sportType === "tennis" || sportType === "football") {
          menuTo = `/${sportType}/tournaments`;
        }
      }

      return {
        ...menu,
        to: menuTo,
        label: menu.label,
        items: menu.submenu
          ?.map((submenu) => {
            let items = [];

            const { params } = submenu;

            if (submenu.type === "tournaments") {
              items = (
                tournamentsQuery?.data.value?.tournaments.filter((x) =>
                  Object.entries(params).every(([key, value]) => {
                    const contains = Array.isArray(value) ? value.includes(x[key]) : x[key] == value;

                    return contains;
                  })
                ) || []
              )
                .filter((x) => x.number_events)
                .map(toTournamentPage);
            }

            if (submenu.type === "popular-events") {
              items = (
                popularEventsQuery?.data.value?.events.filter((x) =>
                  Object.entries(params).every(([key, value]) => {
                    const propertyKey = key == "country" ? "iso_country" : key;

                    const contains = Array.isArray(value) ? value.includes(x[propertyKey]) : x[propertyKey] == value;

                    return contains;
                  })
                ) || []
              ).map(toEventPage);
            }

            if (submenu.type === "upcoming-events") {
              items = (
                upcomingEventsQuery?.data.value?.events.filter((x) =>
                  Object.entries(params).every(([key, value]) => {
                    const propertyKey = key == "country" ? "iso_country" : key;

                    const contains = Array.isArray(value) ? value.includes(x[propertyKey]) : x[propertyKey] == value;

                    return contains;
                  })
                ) || []
              ).map(toEventPage);
            }

            if (submenu.type === "popular-teams") {
              items = (
                popularTeamsQuery?.data.value?.teams.filter((x) =>
                  Object.entries(params).every(([key, value]) => {
                    const contains = Array.isArray(value) ? value.includes(x[key]) : x[key] == value;

                    return contains;
                  })
                ) || []
              ).map(toTeamPage);
            }

            return ((submenu.to || items.length) && { ...submenu, items: items || [] }) || undefined;
          })
          .filter(Boolean),
      };
    })
    .filter((x) => x.items?.length || x.to);
});

const toEventPage = (event) => ({
  label: event.event_name,
  to: toEvent(event),
});

const toTeamPage = (team) => ({
  label: team.official_name,
  to: toTeam(team),
});

// we can have multiple MS/F1/Moto season sales running
const toTournamentPage = (tournament) => {
  const label = ["motosport", "motogp", "formula1"].includes(tournament.sport_type)
    ? tournament.official_name + " (" + tournament.season + ")"
    : tournament.official_name;

  const isInternational =
    tournament.official_name?.toLowerCase().includes("champions league") ||
    tournament.official_name?.toLowerCase().includes("europa league") ||
    tournament.official_name?.toLowerCase().includes("conference league") ||
    tournament.official_name?.toLowerCase().includes("uefa champions") ||
    tournament.official_name?.toLowerCase().includes("uefa europa") ||
    tournament.official_name?.toLowerCase().includes("uefa conference");

  return {
    label,
    region: tournament.region,
    season: tournament.season,
    number_events: tournament.number_events,
    to: toTournament(tournament),
    icon: isInternational ? GlobeAltIcon : undefined,
  };
};
</script>

<style scoped></style>
